import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface SalesChannelRecord {
  id: string;
  code: string;
  name: string;
  channelType: string;
  defaultCurrencyCode: string;
  isActive: boolean;
  organizationUnitId?: string;
}

export interface SalesDocumentProfilePostingAccounts {
  receivableAccountId?: string;
  revenueAccountId?: string;
  inventoryAccountId?: string;
  costOfSalesAccountId?: string;
}

export interface SalesDocumentProfileAdjustmentFactor {
  key?: string;
  name: string;
  operationType: string;
  systemCode: string;
  valueType: string;
  value: number;
  accountId: string;
  sortOrder: number;
}

export interface SalesDocumentProfileFinanceConfig {
  ledgerBookId?: string;
  postingAccounts: SalesDocumentProfilePostingAccounts;
  adjustmentFactors: SalesDocumentProfileAdjustmentFactor[];
}

export interface SalesDocumentProfileRecord {
  id: string;
  code: string;
  name: string;
  documentKind: string;
  defaultCurrencyCode?: string;
  inventoryEffect: string;
  issueFinancialDocument: boolean;
  financeConfig?: SalesDocumentProfileFinanceConfig | null;
  isActive: boolean;
  organizationUnitId?: string;
}

export interface SalesDocumentProfilePrintTemplateRecord {
  printTemplateHtml?: string | null;
  printLayoutJson?: string | null;
  usesDefaultTemplate: boolean;
  defaultPrintTemplateHtml: string;
}

export interface UpdateSalesDocumentProfilePrintTemplatePayload {
  printTemplateHtml?: string | null;
  printLayoutJson?: string | null;
}

export interface SalesOrderPrintRecord {
  html: string;
  fileName: string;
  variables: Record<string, string>;
}

export interface SalesOrderLineRecord {
  id: string;
  lineNumber: number;
  offeringId?: string;
  description: string;
  quantity: number;
  unitListPrice: number;
  unitPrice: number;
  discountAmount: number;
  priceSource?: string;
  lineTotal: number;
}

export interface SalesOrderRecord {
  id: string;
  orderNumber: string;
  status: string;
  salesChannelId: string;
  salesDocumentProfileId: string;
  customerAccountId: string;
  visitPlanId?: string | null;
  currencyCode: string;
  idempotencyKey: string;
  submittedAtUtc?: string;
  cancelledAtUtc?: string;
  cancelReason?: string;
  pricingSnapshotJson?: string;
  lines: SalesOrderLineRecord[];
}

export interface SalesOrderListItem {
  id: string;
  orderNumber: string;
  status: string;
  salesChannelId: string;
  salesDocumentProfileId: string;
  customerAccountId: string;
  currencyCode: string;
  createdAtUtc: string;
}

export interface SalesPagedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

interface PagedApiResponse<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

type ListApiResponse<T> = T[] | PagedApiResponse<T>;

export interface CreateSalesChannelPayload {
  code: string;
  name: string;
  channelType: string;
  defaultCurrencyCode: string;
}

export interface UpdateSalesChannelPayload {
  name: string;
  channelType: string;
  defaultCurrencyCode: string;
  isActive: boolean;
}

export interface CreateSalesDocumentProfilePayload {
  code: string;
  name: string;
  documentKind: string;
  defaultCurrencyCode?: string | null;
  inventoryEffect: string;
  issueFinancialDocument: boolean;
  financeConfig?: SalesDocumentProfileFinanceConfig | null;
}

export interface UpdateSalesDocumentProfilePayload {
  name: string;
  documentKind: string;
  defaultCurrencyCode?: string | null;
  inventoryEffect: string;
  issueFinancialDocument: boolean;
  isActive: boolean;
  financeConfig?: SalesDocumentProfileFinanceConfig | null;
}

export interface SalesOrderLinePayload {
  offeringId?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  unitListPrice?: number;
  discountAmount?: number;
  priceSource?: string;
}

export interface CreateSalesOrderPayload {
  salesChannelId: string;
  salesDocumentProfileId: string;
  customerAccountId: string;
  currencyCode: string;
  idempotencyKey: string;
  pricingSnapshotJson?: string | null;
  visitPlanId?: string | null;
  lines: SalesOrderLinePayload[];
}

export interface UpdateSalesOrderPayload {
  salesChannelId: string;
  salesDocumentProfileId: string;
  customerAccountId: string;
  currencyCode: string;
  pricingSnapshotJson?: string | null;
  lines: SalesOrderLinePayload[];
}

export interface SalesTransitionPayload {
  idempotencyKey: string;
  reason?: string;
  couponCode?: string;
  warehouseId?: string;
  binId?: string;
}

export interface SalesOrderLineInventoryStatus {
  salesOrderLineId: string;
  lineNumber: number;
  offeringId?: string;
  status: string;
  warehouseId?: string;
  binId?: string;
  catalogReservationId?: string;
  inventoryMovementId?: string;
  pickTaskId?: string;
  failureReason?: string;
  fulfillIdempotencyKey?: string;
  availableQuantity?: number;
}

export interface SalesOrderInventoryStatus {
  salesOrderId: string;
  orderStatus: string;
  lines: SalesOrderLineInventoryStatus[];
}

export interface FulfillSalesOrderLinePayload {
  salesOrderLineId: string;
  warehouseId?: string;
  binId?: string;
}

export interface FulfillSalesOrderPayload {
  idempotencyKey: string;
  warehouseId?: string;
  binId?: string;
  lines?: FulfillSalesOrderLinePayload[];
}

export interface FulfillSalesOrderLineResult {
  salesOrderLineId: string;
  status: string;
  inventoryMovementId?: string;
  pickTaskId?: string;
  cogsAmount?: number;
  currencyCode?: string;
}

export interface FulfillSalesOrderResult {
  salesOrderId: string;
  orderStatus: string;
  lines: FulfillSalesOrderLineResult[];
}

export interface SalesOrderStateHistoryRecord {
  id: string;
  fromStatus: string;
  toStatus: string;
  transition: string;
  idempotencyKey?: string;
  occurredAtUtc: string;
  reasonCode?: string;
}

export interface CustomerSegmentRecord {
  id: string;
  customerAccountId: string;
  segmentCode: string;
  effectiveFromUtc: string;
}

export interface CustomerCreditSnapshotRecord {
  id: string;
  customerAccountId: string;
  creditLimit: number;
  exposure: number;
  asOfUtc: string;
}

export interface CreateCustomerSegmentPayload {
  customerAccountId: string;
  segmentCode: string;
  effectiveFromUtc?: string;
}

export interface CreateCustomerCreditSnapshotPayload {
  customerAccountId: string;
  creditLimit: number;
  exposure: number;
  asOfUtc?: string;
}

export interface ChannelPolicyBindingRecord {
  id: string;
  policyKey: string;
  isEnabled: boolean;
  configJson?: string;
}

export interface UpsertChannelPolicyBindingPayload {
  policyKey: string;
  isEnabled: boolean;
  configJson?: string;
}

export interface PriceListItemRecord {
  id: string;
  offeringId: string;
  unitPrice: number;
  minUnitPrice?: number;
}

export interface PriceListRecord {
  id: string;
  code: string;
  name: string;
  currencyCode: string;
  validFromUtc: string;
  validToUtc?: string;
  maxDiscountPercent?: number;
  minMarginPercent?: number;
  isActive: boolean;
  items: PriceListItemRecord[];
}

export interface PriceListListItem {
  id: string;
  code: string;
  name: string;
  currencyCode: string;
  isActive: boolean;
  validFromUtc: string;
  validToUtc?: string;
}

export interface PriceListItemPayload {
  offeringId: string;
  unitPrice: number;
  minUnitPrice?: number;
}

export interface CreatePriceListPayload {
  code: string;
  name: string;
  currencyCode: string;
  validFromUtc?: string;
  validToUtc?: string;
  maxDiscountPercent?: number | null;
  minMarginPercent?: number | null;
  items?: PriceListItemPayload[];
}

export interface UpdatePriceListPayload {
  name: string;
  currencyCode: string;
  validFromUtc?: string;
  validToUtc?: string;
  maxDiscountPercent?: number | null;
  minMarginPercent?: number | null;
  isActive: boolean;
  items?: PriceListItemPayload[];
}

export interface PricingQuoteLine {
  lineNumber: number;
  offeringId?: string;
  quantity: number;
  listUnitPrice: number;
  unitPrice: number;
  lineTotal: number;
  discountAmount: number;
  discountPercent: number;
  priceSource: string;
}

export interface PricingQuoteResult {
  priceListId?: string;
  currencyCode: string;
  subtotal: number;
  totalDiscount: number;
  total: number;
  lines: PricingQuoteLine[];
  snapshotJson: string;
  couponId?: string;
  couponCode?: string;
  promotionDiscount?: number;
}

export interface PromotionCampaignListItem {
  id: string;
  code: string;
  name: string;
  currencyCode: string;
  isActive: boolean;
  budgetAmount?: number;
  budgetConsumed: number;
  validFromUtc: string;
  validToUtc?: string;
}

export interface PromotionCampaignRecord {
  id: string;
  code: string;
  name: string;
  currencyCode: string;
  validFromUtc: string;
  validToUtc?: string;
  budgetAmount?: number;
  budgetConsumed: number;
  isActive: boolean;
}

export interface CreatePromotionCampaignPayload {
  code: string;
  name: string;
  currencyCode: string;
  validFromUtc?: string;
  validToUtc?: string;
  budgetAmount?: number | null;
}

export interface UpdatePromotionCampaignPayload {
  name: string;
  currencyCode: string;
  validFromUtc?: string;
  validToUtc?: string;
  budgetAmount?: number | null;
  isActive: boolean;
}

export interface CouponListItem {
  id: string;
  code: string;
  campaignId?: string;
  discountType: string;
  discountValue: number;
  isActive: boolean;
  redemptionCount: number;
  maxRedemptions?: number;
  validFromUtc: string;
  validToUtc?: string;
}

export interface CouponRecord {
  id: string;
  code: string;
  campaignId?: string;
  discountType: string;
  discountValue: number;
  minOrderTotal?: number;
  maxRedemptions?: number;
  redemptionCount: number;
  validFromUtc: string;
  validToUtc?: string;
  isActive: boolean;
}

export interface CreateCouponPayload {
  code: string;
  campaignId?: string | null;
  discountType: string;
  discountValue: number;
  minOrderTotal?: number | null;
  maxRedemptions?: number | null;
  validFromUtc?: string;
  validToUtc?: string;
}

export interface UpdateCouponPayload {
  campaignId?: string | null;
  discountType: string;
  discountValue: number;
  minOrderTotal?: number | null;
  maxRedemptions?: number | null;
  validFromUtc?: string;
  validToUtc?: string;
  isActive: boolean;
}

export interface PolicyEvaluationResult {
  allowed: boolean;
  violations: string[];
  waivedViolations: string[];
  pendingOverrideCount: number;
}

export interface PricingHintRecord {
  code: string;
  message: string;
  confidence?: number | null;
  suggestedAction?: string | null;
}

export interface PricingOverrideRecord {
  id: string;
  salesOrderId: string;
  violationCode: string;
  reason: string;
  status: string;
  requestedByUserId: string;
  reviewedByUserId?: string | null;
  reviewedAtUtc?: string | null;
  createdAtUtc: string;
}

export interface CreatePricingOverridePayload {
  violationCode: string;
  reason: string;
}

export interface SalesTerritoryRecord {
  id: string;
  parentTerritoryId?: string | null;
  code: string;
  name: string;
  level: string;
  isActive: boolean;
  organizationUnitId?: string;
}

export interface CreateSalesTerritoryPayload {
  parentTerritoryId?: string | null;
  code: string;
  name: string;
  level: string;
}

export interface UpdateSalesTerritoryPayload {
  name: string;
  isActive: boolean;
}

export interface TerritoryAssignmentRecord {
  id: string;
  salesTerritoryId: string;
  subjectType: string;
  subjectId: string;
  isPrimary: boolean;
  effectiveFromUtc: string;
  effectiveToUtc?: string | null;
  organizationUnitId?: string;
}

export interface CreateTerritoryAssignmentPayload {
  subjectType: string;
  subjectId: string;
  isPrimary: boolean;
  effectiveFromUtc?: string | null;
  effectiveToUtc?: string | null;
}

export interface UpdateTerritoryAssignmentPayload {
  isPrimary: boolean;
  effectiveFromUtc: string;
  effectiveToUtc?: string | null;
}

export interface SalesQuotaRecord {
  id: string;
  code: string;
  name: string;
  scopeType: string;
  scopeId: string;
  metricType: string;
  currencyCode?: string | null;
  periodType: string;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  limitValue: number;
  isActive: boolean;
  organizationUnitId?: string;
}

export interface CreateSalesQuotaPayload {
  code: string;
  name: string;
  scopeType: string;
  scopeId: string;
  metricType: string;
  currencyCode?: string | null;
  periodType: string;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  limitValue: number;
}

export interface UpdateSalesQuotaPayload {
  name: string;
  metricType: string;
  currencyCode?: string | null;
  periodType: string;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  limitValue: number;
  isActive: boolean;
}

export interface QuotaConsumptionRecord {
  id: string;
  salesQuotaId: string;
  consumedValue: number;
  consumedAtUtc: string;
  referenceType: string;
  referenceId?: string | null;
  idempotencyKey?: string | null;
  notes?: string | null;
}

export interface CreateQuotaConsumptionPayload {
  consumedValue: number;
  consumedAtUtc?: string | null;
  referenceType: string;
  referenceId?: string | null;
  idempotencyKey?: string | null;
  notes?: string | null;
}

export interface EvaluateSalesQuotaLinePayload {
  offeringId?: string | null;
  quantity: number;
  amount: number;
}

export interface EvaluateSalesQuotaPayload {
  salesChannelId?: string | null;
  customerAccountId?: string | null;
  sellerUserId?: string | null;
  currencyCode?: string | null;
  asOfUtc?: string | null;
  lines?: EvaluateSalesQuotaLinePayload[];
}

export interface SalesQuotaUtilizationRecord {
  quotaId: string;
  code: string;
  scopeType: string;
  scopeId: string;
  metricType: string;
  limitValue: number;
  consumedValue: number;
  proposedValue: number;
  remainingValue: number;
  allowed: boolean;
}

export interface EvaluateSalesQuotaResult {
  allowed: boolean;
  violations: string[];
  quotas: SalesQuotaUtilizationRecord[];
}

export interface SalesReservationRecord {
  id: string;
  reservationKey: string;
  reservationType: string;
  status: string;
  salesChannelId?: string | null;
  customerAccountId?: string | null;
  offeringId?: string | null;
  reservedQuantity: number;
  reservedAmount?: number | null;
  currencyCode?: string | null;
  heldAtUtc: string;
  expiresAtUtc: string;
  idempotencyKey: string;
  notes?: string | null;
  organizationUnitId?: string;
}

export interface ReservationHoldRecord {
  id: string;
  salesReservationId: string;
  heldQuantity: number;
  heldAmount?: number | null;
  heldAtUtc: string;
}

export interface ReservationReleaseRecord {
  id: string;
  salesReservationId: string;
  releasedAtUtc: string;
  reason?: string | null;
}

export interface CreateSalesReservationPayload {
  reservationKey: string;
  reservationType: string;
  salesChannelId?: string | null;
  customerAccountId?: string | null;
  offeringId?: string | null;
  reservedQuantity: number;
  reservedAmount?: number | null;
  currencyCode?: string | null;
  heldAtUtc?: string | null;
  expiresAtUtc: string;
  idempotencyKey: string;
  notes?: string | null;
}

export interface ReleaseSalesReservationPayload {
  reason?: string | null;
  idempotencyKey?: string | null;
}

export interface AllocationPolicyRecord {
  id: string;
  code: string;
  name: string;
  scopeType: string;
  scopeId?: string | null;
  maxReservedQuantity: number;
  maxReservedAmount?: number | null;
  requireQuotaPass: boolean;
  priority: number;
  isActive: boolean;
  organizationUnitId?: string;
}

export interface CreateAllocationPolicyPayload {
  code: string;
  name: string;
  scopeType: string;
  scopeId?: string | null;
  maxReservedQuantity: number;
  maxReservedAmount?: number | null;
  requireQuotaPass: boolean;
  priority: number;
}

export interface UpdateAllocationPolicyPayload {
  name: string;
  maxReservedQuantity: number;
  maxReservedAmount?: number | null;
  requireQuotaPass: boolean;
  priority: number;
  isActive: boolean;
}

export interface EvaluateAllocationLinePayload {
  offeringId?: string | null;
  quantity: number;
  amount: number;
}

export interface EvaluateAllocationPayload {
  salesChannelId?: string | null;
  customerAccountId?: string | null;
  sellerUserId?: string | null;
  currencyCode?: string | null;
  correlationKey?: string | null;
  lines?: EvaluateAllocationLinePayload[];
}

export interface AllocationPolicyEvaluationRecord {
  policyId: string;
  code: string;
  scopeType: string;
  scopeId?: string | null;
  maxReservedQuantity: number;
  maxReservedAmount?: number | null;
  activeReservedQuantity: number;
  activeReservedAmount?: number | null;
  proposedQuantity: number;
  proposedAmount?: number | null;
  allowed: boolean;
}

export interface EvaluateAllocationResult {
  allowed: boolean;
  violations: string[];
  policies: AllocationPolicyEvaluationRecord[];
  decisionId: string;
}

export interface CreditPolicyRecord {
  id: string;
  code: string;
  name: string;
  scopeType: string;
  scopeId?: string | null;
  defaultCreditLimit?: number | null;
  blockWhenOverdue: boolean;
  maxRiskScore?: number | null;
  priority: number;
  isActive: boolean;
  organizationUnitId?: string;
}

export interface CreateCreditPolicyPayload {
  code: string;
  name: string;
  scopeType: string;
  scopeId?: string | null;
  defaultCreditLimit?: number | null;
  blockWhenOverdue: boolean;
  maxRiskScore?: number | null;
  priority: number;
}

export interface UpdateCreditPolicyPayload {
  name: string;
  defaultCreditLimit?: number | null;
  blockWhenOverdue: boolean;
  maxRiskScore?: number | null;
  priority: number;
  isActive: boolean;
}

export interface CustomerCreditLimitRecord {
  id: string;
  customerAccountId: string;
  creditLimit: number;
  currencyCode: string;
  effectiveFromUtc?: string | null;
  effectiveUntilUtc?: string | null;
  isActive: boolean;
  notes?: string | null;
  organizationUnitId?: string;
}

export interface CreateCustomerCreditLimitPayload {
  customerAccountId: string;
  creditLimit: number;
  currencyCode: string;
  effectiveFromUtc?: string | null;
  effectiveUntilUtc?: string | null;
  notes?: string | null;
}

export interface UpdateCustomerCreditLimitPayload {
  creditLimit: number;
  currencyCode: string;
  effectiveFromUtc?: string | null;
  effectiveUntilUtc?: string | null;
  isActive: boolean;
  notes?: string | null;
}

export interface EvaluateCreditPayload {
  customerAccountId: string;
  salesChannelId?: string | null;
  currencyCode?: string | null;
  proposedAmount: number;
  correlationKey?: string | null;
}

export interface CreditPolicyEvaluationRecord {
  policyId: string;
  code: string;
  scopeType: string;
  scopeId?: string | null;
  defaultCreditLimit?: number | null;
  blockWhenOverdue: boolean;
  maxRiskScore?: number | null;
  allowed: boolean;
}

export interface EvaluateCreditResult {
  allowed: boolean;
  violations: string[];
  effectiveLimit: number;
  currentExposure: number;
  availableCredit: number;
  proposedAmount: number;
  policies: CreditPolicyEvaluationRecord[];
  decisionId: string;
}

export interface CreditDecisionRecord {
  id: string;
  creditPolicyId?: string | null;
  customerAccountId: string;
  salesChannelId?: string | null;
  currencyCode: string;
  allowed: boolean;
  proposedAmount: number;
  effectiveLimit: number;
  currentExposure: number;
  availableCredit: number;
  platformCreditLimit?: number | null;
  platformExposure?: number | null;
  platformIsOverdue: boolean;
  platformRiskScore?: number | null;
  violations: string[];
  evaluatedAtUtc: string;
  correlationKey: string;
}

export interface InstallmentPlanRecord {
  id: string;
  planNumber: string;
  customerAccountId: string;
  salesOrderId?: string | null;
  principalAmount: number;
  totalAmount: number;
  hasInterest: boolean;
  feeRate: number;
  feePeriod: string;
  currencyCode: string;
  installmentCount: number;
  frequency: string;
  firstDueDateUtc: string;
  status: string;
  notes?: string | null;
}

export interface InstallmentScheduleRecord {
  id: string;
  installmentPlanId: string;
  sequenceNumber: number;
  dueDateUtc: string;
  scheduledAmount: number;
  paidAmount: number;
  remainingAmount: number;
  status: string;
}

export interface InstallmentPaymentRecord {
  id: string;
  installmentPlanId: string;
  installmentScheduleId: string;
  amount: number;
  paidAtUtc: string;
  notes?: string | null;
}

export interface CreateInstallmentPlanPayload {
  planNumber: string;
  customerAccountId: string;
  salesOrderId?: string | null;
  totalAmount: number;
  hasInterest: boolean;
  feeRate: number;
  feePeriod: string;
  currencyCode: string;
  installmentCount: number;
  frequency: string;
  firstDueDateUtc: string;
  notes?: string | null;
}

export interface RecordInstallmentPaymentPayload {
  installmentScheduleId: string;
  amount: number;
  paidAtUtc?: string | null;
  idempotencyKey?: string | null;
  notes?: string | null;
}

export interface CommissionPlanRecord {
  id: string;
  planCode: string;
  name: string;
  currencyCode: string;
  status: string;
  effectiveFromUtc: string;
  effectiveUntilUtc?: string | null;
  targetAmount?: number | null;
  notes?: string | null;
}

export interface CommissionRuleRecord {
  id: string;
  commissionPlanId: string;
  sequence: number;
  name: string;
  ruleType: string;
  ratePercent: number;
  fixedAmount: number;
  minBasisAmount: number;
  maxCommissionAmount?: number | null;
  salesChannelId?: string | null;
  isActive: boolean;
}

export interface CommissionPayoutRecord {
  id: string;
  commissionPlanId: string;
  salesOrderId?: string | null;
  payeeUserId?: string | null;
  basisAmount: number;
  commissionAmount: number;
  clawedBackAmount: number;
  netAmount: number;
  currencyCode: string;
  periodStartUtc: string;
  periodEndUtc: string;
  status: string;
  idempotencyKey: string;
  calculatedAtUtc: string;
  notes?: string | null;
}

export interface CommissionClawbackRecord {
  id: string;
  commissionPayoutId: string;
  salesOrderId?: string | null;
  amount: number;
  reason: string;
  clawbackAtUtc: string;
}

export interface CreateCommissionPlanRulePayload {
  sequence: number;
  name: string;
  ruleType: string;
  ratePercent: number;
  fixedAmount: number;
  minBasisAmount: number;
  maxCommissionAmount?: number | null;
  salesChannelId?: string | null;
  isActive: boolean;
}

export interface CreateCommissionPlanPayload {
  planCode: string;
  name: string;
  currencyCode: string;
  effectiveFromUtc: string;
  effectiveUntilUtc?: string | null;
  targetAmount?: number | null;
  notes?: string | null;
  rules: CreateCommissionPlanRulePayload[];
}

export interface CalculateCommissionPayload {
  commissionPlanId: string;
  salesOrderId?: string | null;
  basisAmount?: number | null;
  payeeUserId?: string | null;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  idempotencyKey: string;
  persist: boolean;
}

export interface CommissionRuleBreakdownRecord {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  appliedAmount: number;
}

export interface CalculateCommissionResult {
  payoutId?: string | null;
  basisAmount: number;
  commissionAmount: number;
  breakdown: CommissionRuleBreakdownRecord[];
  persisted: boolean;
}

export interface RecordCommissionClawbackPayload {
  commissionPayoutId: string;
  amount: number;
  reason: string;
  clawbackAtUtc?: string | null;
  idempotencyKey?: string | null;
}

export interface EnterpriseContractRecord {
  id: string;
  contractNumber: string;
  customerAccountId: string;
  salesChannelId?: string | null;
  proformaSalesOrderId?: string | null;
  currencyCode: string;
  totalContractValue: number;
  status: string;
  effectiveFromUtc: string;
  effectiveUntilUtc?: string | null;
  notes?: string | null;
}

export interface ContractReleaseOrderRecord {
  id: string;
  enterpriseContractId: string;
  salesOrderId?: string | null;
  releaseNumber: string;
  releaseAmount: number;
  status: string;
  releasedAtUtc?: string | null;
  notes?: string | null;
}

export interface CreateEnterpriseContractPayload {
  contractNumber: string;
  customerAccountId: string;
  salesChannelId?: string | null;
  proformaSalesOrderId?: string | null;
  currencyCode: string;
  totalContractValue: number;
  effectiveFromUtc: string;
  effectiveUntilUtc?: string | null;
  notes?: string | null;
}

export interface CreateContractReleaseOrderPayload {
  releaseNumber: string;
  releaseAmount: number;
  salesOrderId?: string | null;
  notes?: string | null;
  releaseImmediately: boolean;
}

export interface TenderOpportunityRecord {
  id: string;
  tenderNumber: string;
  title: string;
  customerAccountId?: string | null;
  status: string;
  dueDateUtc?: string | null;
  estimatedValue: number;
  currencyCode: string;
  notes?: string | null;
}

export interface TenderProposalRecord {
  id: string;
  tenderOpportunityId: string;
  proposalNumber: string;
  versionNumber: number;
  status: string;
  proposalAmount: number;
  submittedAtUtc?: string | null;
  notes?: string | null;
}

export interface CreateTenderPayload {
  tenderNumber: string;
  title: string;
  customerAccountId?: string | null;
  dueDateUtc?: string | null;
  estimatedValue: number;
  currencyCode: string;
  notes?: string | null;
}

export interface CreateTenderProposalPayload {
  proposalNumber: string;
  proposalAmount: number;
  notes?: string | null;
  submitImmediately: boolean;
}

export interface ConsignmentContractRecord {
  id: string;
  contractNumber: string;
  customerAccountId: string;
  salesChannelId?: string | null;
  effectiveFromUtc: string;
  effectiveUntilUtc?: string | null;
  status: string;
  notes?: string | null;
}

export interface ConsignmentStockRecord {
  id: string;
  consignmentContractId: string;
  offeringId: string;
  quantityOnHand: number;
  quantitySettled: number;
  remainingQuantity: number;
}

export interface ConsignmentSettlementRecord {
  id: string;
  consignmentContractId: string;
  settledQuantity: number;
  settledAmount: number;
  currencyCode: string;
  settledAtUtc: string;
  notes?: string | null;
}

export interface ConsignmentStockLinePayload {
  offeringId: string;
  quantityOnHand: number;
}

export interface CreateConsignmentContractPayload {
  contractNumber: string;
  customerAccountId: string;
  salesChannelId?: string | null;
  effectiveFromUtc: string;
  effectiveUntilUtc?: string | null;
  notes?: string | null;
  stockLines?: ConsignmentStockLinePayload[];
}

export interface SettleConsignmentPayload {
  consignmentStockId?: string | null;
  offeringId?: string | null;
  settledQuantity: number;
  settledAmount: number;
  currencyCode: string;
  settledAtUtc?: string | null;
  idempotencyKey?: string | null;
  notes?: string | null;
}

export interface CollectionCaseRecord {
  id: string;
  caseNumber: string;
  customerAccountId: string;
  status: string;
  totalOverdueAmount: number;
  currencyCode: string;
  riskScore?: number | null;
  openedAtUtc: string;
  closedAtUtc?: string | null;
  notes?: string | null;
}

export interface CollectionActionRecord {
  id: string;
  collectionCaseId: string;
  actionType: string;
  notes: string;
  performedAtUtc: string;
}

export interface CreateCollectionCasePayload {
  caseNumber: string;
  customerAccountId: string;
  totalOverdueAmount: number;
  currencyCode: string;
  riskScore?: number | null;
  openedAtUtc?: string | null;
  notes?: string | null;
}

export interface UpdateCollectionCasePayload {
  status: string;
  notes?: string | null;
}

export interface CreateCollectionActionPayload {
  actionType: string;
  notes: string;
  performedAtUtc?: string | null;
}

export interface RoutePlanRecord {
  id: string;
  routeNumber: string;
  plannedDateUtc: string;
  geographicRouteId?: string | null;
  assignedSellerId?: string | null;
  status: string;
  notes?: string | null;
  visitCount: number;
  completedVisitCount: number;
}

export interface VisitExecutionRecord {
  id: string;
  visitPlanId: string;
  status: string;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
  outcomeNotes?: string | null;
  checkInLatitude?: number | null;
  checkInLongitude?: number | null;
}

export interface VisitPlanRecord {
  id: string;
  routePlanId: string;
  sequenceNumber: number;
  customerAccountId: string;
  plannedArrivalUtc?: string | null;
  plannedDepartureUtc?: string | null;
  status: string;
  execution?: VisitExecutionRecord | null;
}

export interface VisitPlanLinePayload {
  sequenceNumber: number;
  customerAccountId: string;
  plannedArrivalUtc?: string | null;
  plannedDepartureUtc?: string | null;
}

export interface CreateRoutePlanPayload {
  routeNumber?: string | null;
  plannedDateUtc: string;
  geographicRouteId?: string | null;
  assignedSellerId?: string | null;
  notes?: string | null;
  publishImmediately?: boolean;
  visitPlans?: VisitPlanLinePayload[];
}

export interface CreateVisitPlanPayload {
  routePlanId: string;
  sequenceNumber: number;
  customerAccountId: string;
  plannedArrivalUtc?: string | null;
  plannedDepartureUtc?: string | null;
}

export interface CompleteVisitExecutionPayload {
  completedAtUtc?: string | null;
  outcomeNotes?: string | null;
  checkInLatitude?: number | null;
  checkInLongitude?: number | null;
}

export interface VanStockBalanceRecord {
  id: string;
  routePlanId: string;
  offeringId: string;
  quantityOnHand: number;
}

export interface VanStockMovementRecord {
  id: string;
  routePlanId: string;
  offeringId: string;
  movementType: string;
  quantityDelta: number;
  quantityAfter: number;
  occurredAtUtc: string;
}

export interface CreateVanStockMovementPayload {
  routePlanId: string;
  offeringId: string;
  movementType: string;
  quantityDelta: number;
  visitPlanId?: string | null;
  idempotencyKey?: string | null;
}

export interface ProofOfVisitRecord {
  id: string;
  visitPlanId: string;
  capturedAtUtc: string;
  contentHash: string;
  notes?: string | null;
}

export interface CreateProofOfVisitPayload {
  visitPlanId: string;
  payloadCanonical: string;
  contentHash?: string | null;
  notes?: string | null;
}

export interface ProofOfDeliveryRecord {
  id: string;
  customerAccountId: string;
  visitPlanId?: string | null;
  capturedAtUtc: string;
  contentHash: string;
}

export interface RetailAuditRecord {
  id: string;
  visitPlanId: string;
  customerAccountId: string;
  auditType: string;
  status: string;
  auditedAtUtc: string;
  complianceScore?: number | null;
  notes?: string | null;
}

export interface CreateRetailAuditPayload {
  visitPlanId: string;
  auditType: string;
  complianceScore?: number | null;
  notes?: string | null;
}

export interface StoreImageEvidenceRecord {
  id: string;
  retailAuditId: string;
  imageReference: string;
  contentHash: string;
  caption?: string | null;
}

export interface FieldSettlementRecord {
  id: string;
  routePlanId: string;
  settlementNumber: string;
  cashCollectedAmount: number;
  currencyCode: string;
  settledAtUtc: string;
  status: string;
}

export interface CreateFieldSettlementPayload {
  routePlanId: string;
  settlementNumber: string;
  cashCollectedAmount: number;
  currencyCode: string;
  idempotencyKey?: string | null;
  notes?: string | null;
}

@Injectable({ providedIn: 'root' })
export class SalesApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/sales`;
  private readonly distributionBase = `${environment.apiBaseUrl}/api/v1/distribution`;

  listChannels(): Observable<SalesChannelRecord[]> {
    return this.http
      .get<ListApiResponse<SalesChannelRecord>>(`${this.base}/channels`)
      .pipe(map(response => this.unwrapList(response)));
  }

  getChannel(id: string): Observable<SalesChannelRecord> {
    return this.http.get<SalesChannelRecord>(`${this.base}/channels/${id}`);
  }

  createChannel(payload: CreateSalesChannelPayload): Observable<SalesChannelRecord> {
    return this.http.post<SalesChannelRecord>(`${this.base}/channels`, payload);
  }

  updateChannel(id: string, payload: UpdateSalesChannelPayload): Observable<SalesChannelRecord> {
    return this.http.put<SalesChannelRecord>(`${this.base}/channels/${id}`, payload);
  }

  listDocumentProfiles(): Observable<SalesDocumentProfileRecord[]> {
    return this.http
      .get<ListApiResponse<SalesDocumentProfileRecord>>(`${this.base}/document-profiles`)
      .pipe(map(response => this.unwrapList(response)));
  }

  getDocumentProfile(id: string): Observable<SalesDocumentProfileRecord> {
    return this.http.get<SalesDocumentProfileRecord>(`${this.base}/document-profiles/${id}`);
  }

  createDocumentProfile(payload: CreateSalesDocumentProfilePayload): Observable<SalesDocumentProfileRecord> {
    return this.http.post<SalesDocumentProfileRecord>(`${this.base}/document-profiles`, payload);
  }

  updateDocumentProfile(id: string, payload: UpdateSalesDocumentProfilePayload): Observable<SalesDocumentProfileRecord> {
    return this.http.put<SalesDocumentProfileRecord>(`${this.base}/document-profiles/${id}`, payload);
  }

  deleteDocumentProfile(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/document-profiles/${id}`);
  }

  getDocumentProfilePrintTemplate(id: string): Observable<SalesDocumentProfilePrintTemplateRecord> {
    return this.http.get<SalesDocumentProfilePrintTemplateRecord>(`${this.base}/document-profiles/${id}/print-template`);
  }

  updateDocumentProfilePrintTemplate(id: string, payload: UpdateSalesDocumentProfilePrintTemplatePayload): Observable<SalesDocumentProfilePrintTemplateRecord> {
    return this.http.put<SalesDocumentProfilePrintTemplateRecord>(`${this.base}/document-profiles/${id}/print-template`, payload);
  }

  previewDocumentProfilePrintTemplate(id: string): Observable<SalesOrderPrintRecord> {
    return this.http.post<SalesOrderPrintRecord>(`${this.base}/document-profiles/${id}/print-template/preview`, {});
  }

  getOrderPrint(orderId: string): Observable<SalesOrderPrintRecord> {
    return this.http.get<SalesOrderPrintRecord>(`${this.base}/orders/${orderId}/print`);
  }

  listOrders(query?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    channelId?: string;
    visitPlanId?: string;
    customerAccountId?: string;
    documentKind?: string;
  }): Observable<SalesPagedResult<SalesOrderListItem>> {
    let params = new HttpParams();
    if (query?.page) params = params.set('page', String(query.page));
    if (query?.pageSize) params = params.set('pageSize', String(query.pageSize));
    if (query?.search) params = params.set('search', query.search);
    if (query?.status) params = params.set('status', query.status);
    if (query?.channelId) params = params.set('channelId', query.channelId);
    if (query?.visitPlanId) params = params.set('visitPlanId', query.visitPlanId);
    if (query?.customerAccountId) params = params.set('customerAccountId', query.customerAccountId);
    if (query?.documentKind) params = params.set('documentKind', query.documentKind);

    return this.http
      .get<PagedApiResponse<SalesOrderListItem>>(`${this.base}/orders`, { params })
      .pipe(
        map(response => ({
          items: response.items ?? [],
          pageNumber: response.pageNumber ?? 1,
          pageSize: response.pageSize ?? 25,
          totalCount: response.totalCount ?? 0
        }))
      );
  }

  previewOrderNumber(salesDocumentProfileId: string): Observable<string> {
    const params = new HttpParams().set('salesDocumentProfileId', salesDocumentProfileId);
    return this.http
      .get<{ preview?: string; Preview?: string }>(`${this.base}/orders/preview-order-number`, { params })
      .pipe(map(response => (response.preview ?? response.Preview ?? '').trim()));
  }

  getOrder(id: string): Observable<SalesOrderRecord> {
    return this.http.get<SalesOrderRecord>(`${this.base}/orders/${id}`);
  }

  createOrder(payload: CreateSalesOrderPayload): Observable<SalesOrderRecord> {
    return this.http.post<SalesOrderRecord>(`${this.base}/orders`, payload);
  }

  updateOrder(id: string, payload: UpdateSalesOrderPayload): Observable<SalesOrderRecord> {
    return this.http.put<SalesOrderRecord>(`${this.base}/orders/${id}`, payload);
  }

  submitOrder(id: string, payload: SalesTransitionPayload): Observable<SalesOrderRecord> {
    return this.http.post<SalesOrderRecord>(`${this.base}/orders/${id}/submit`, payload);
  }

  cancelOrder(id: string, payload: SalesTransitionPayload): Observable<SalesOrderRecord> {
    return this.http.post<SalesOrderRecord>(`${this.base}/orders/${id}/cancel`, payload);
  }

  getOrderInventoryStatus(orderId: string): Observable<SalesOrderInventoryStatus> {
    return this.http.get<SalesOrderInventoryStatus>(`${this.base}/orders/${orderId}/inventory-status`);
  }

  fulfillOrder(orderId: string, payload: FulfillSalesOrderPayload): Observable<FulfillSalesOrderResult> {
    return this.http.post<FulfillSalesOrderResult>(`${this.base}/orders/${orderId}/fulfill`, payload);
  }

  convertProformaToInvoice(orderId: string): Observable<SalesOrderRecord> {
    return this.http.post<SalesOrderRecord>(`${this.base}/orders/${orderId}/convert-to-invoice`, {});
  }

  convertProformaToContract(orderId: string): Observable<EnterpriseContractRecord> {
    return this.http.post<EnterpriseContractRecord>(`${this.base}/orders/${orderId}/convert-to-contract`, {});
  }

  listOrderStateHistory(orderId: string): Observable<SalesOrderStateHistoryRecord[]> {
    return this.http
      .get<ListApiResponse<SalesOrderStateHistoryRecord>>(`${this.base}/orders/${orderId}/state-history`)
      .pipe(map(response => this.unwrapList(response)));
  }

  listChannelPolicyBindings(channelId: string): Observable<ChannelPolicyBindingRecord[]> {
    return this.http
      .get<ListApiResponse<ChannelPolicyBindingRecord>>(`${this.base}/channels/${channelId}/policy-bindings`)
      .pipe(map(response => this.unwrapList(response)));
  }

  replaceChannelPolicyBindings(
    channelId: string,
    bindings: UpsertChannelPolicyBindingPayload[]
  ): Observable<ChannelPolicyBindingRecord[]> {
    return this.http.put<ChannelPolicyBindingRecord[]>(`${this.base}/channels/${channelId}/policy-bindings`, { bindings });
  }

  listCustomerSegments(customerAccountId?: string): Observable<CustomerSegmentRecord[]> {
    let params = new HttpParams();
    if (customerAccountId) params = params.set('customerAccountId', customerAccountId);
    return this.http
      .get<ListApiResponse<CustomerSegmentRecord>>(`${this.base}/customer-segments`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  createCustomerSegment(payload: CreateCustomerSegmentPayload): Observable<CustomerSegmentRecord> {
    return this.http.post<CustomerSegmentRecord>(`${this.base}/customer-segments`, payload);
  }

  listCustomerCreditSnapshots(customerAccountId?: string): Observable<CustomerCreditSnapshotRecord[]> {
    let params = new HttpParams();
    if (customerAccountId) params = params.set('customerAccountId', customerAccountId);
    return this.http
      .get<ListApiResponse<CustomerCreditSnapshotRecord>>(`${this.base}/customer-credit-snapshots`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  createCustomerCreditSnapshot(payload: CreateCustomerCreditSnapshotPayload): Observable<CustomerCreditSnapshotRecord> {
    return this.http.post<CustomerCreditSnapshotRecord>(`${this.base}/customer-credit-snapshots`, payload);
  }

  listPriceLists(): Observable<PriceListListItem[]> {
    return this.http
      .get<ListApiResponse<PriceListListItem>>(`${this.base}/price-lists`)
      .pipe(map(response => this.unwrapList(response)));
  }

  getPriceList(id: string): Observable<PriceListRecord> {
    return this.http.get<PriceListRecord>(`${this.base}/price-lists/${id}`);
  }

  listPriceListItems(priceListId: string): Observable<PriceListItemRecord[]> {
    return this.http.get<PriceListItemRecord[]>(`${this.base}/price-lists/${priceListId}/items`);
  }

  createPriceList(payload: CreatePriceListPayload): Observable<PriceListRecord> {
    return this.http.post<PriceListRecord>(`${this.base}/price-lists`, payload);
  }

  updatePriceList(id: string, payload: UpdatePriceListPayload): Observable<PriceListRecord> {
    return this.http.put<PriceListRecord>(`${this.base}/price-lists/${id}`, payload);
  }

  quotePricing(payload: {
    priceListId?: string;
    salesChannelId?: string;
    currencyCode: string;
    couponCode?: string;
    promotionCampaignId?: string;
    lines: { offeringId?: string; quantity: number; requestedUnitPrice?: number }[];
  }): Observable<PricingQuoteResult> {
    return this.http.post<PricingQuoteResult>(`${this.base}/pricing/quote`, payload);
  }

  listPromotionCampaigns(): Observable<PromotionCampaignListItem[]> {
    return this.http.get<PromotionCampaignListItem[]>(`${this.base}/promotion-campaigns`);
  }

  getPromotionCampaign(id: string): Observable<PromotionCampaignRecord> {
    return this.http.get<PromotionCampaignRecord>(`${this.base}/promotion-campaigns/${id}`);
  }

  createPromotionCampaign(payload: CreatePromotionCampaignPayload): Observable<PromotionCampaignRecord> {
    return this.http.post<PromotionCampaignRecord>(`${this.base}/promotion-campaigns`, payload);
  }

  updatePromotionCampaign(id: string, payload: UpdatePromotionCampaignPayload): Observable<PromotionCampaignRecord> {
    return this.http.put<PromotionCampaignRecord>(`${this.base}/promotion-campaigns/${id}`, payload);
  }

  listCoupons(campaignId?: string): Observable<CouponListItem[]> {
    let params = new HttpParams();
    if (campaignId) params = params.set('campaignId', campaignId);
    return this.http.get<CouponListItem[]>(`${this.base}/coupons`, { params });
  }

  getCoupon(id: string): Observable<CouponRecord> {
    return this.http.get<CouponRecord>(`${this.base}/coupons/${id}`);
  }

  createCoupon(payload: CreateCouponPayload): Observable<CouponRecord> {
    return this.http.post<CouponRecord>(`${this.base}/coupons`, payload);
  }

  updateCoupon(id: string, payload: UpdateCouponPayload): Observable<CouponRecord> {
    return this.http.put<CouponRecord>(`${this.base}/coupons/${id}`, payload);
  }

  validateCoupon(payload: {
    code: string;
    currencyCode: string;
    orderTotal: number;
    campaignId?: string;
  }): Observable<PolicyEvaluationResult & { promotionDiscount?: number; couponId?: string }> {
    return this.http.post<PolicyEvaluationResult & { promotionDiscount?: number; couponId?: string }>(
      `${this.base}/promotions/coupons/validate`,
      payload
    );
  }

  quoteOrderPricing(orderId: string, priceListId?: string, couponCode?: string): Observable<PricingQuoteResult> {
    let params = new HttpParams();
    if (priceListId) params = params.set('priceListId', priceListId);
    if (couponCode?.trim()) params = params.set('couponCode', couponCode.trim());
    return this.http.post<PricingQuoteResult>(`${this.base}/orders/${orderId}/quote-pricing`, {}, { params });
  }

  evaluateOrderPolicies(orderId: string, priceListId?: string, couponCode?: string): Observable<PolicyEvaluationResult> {
    let params = new HttpParams();
    if (priceListId) params = params.set('priceListId', priceListId);
    if (couponCode?.trim()) params = params.set('couponCode', couponCode.trim());
    return this.http.post<PolicyEvaluationResult>(`${this.base}/orders/${orderId}/evaluate-policies`, {}, { params });
  }

  listOrderPricingHints(orderId: string, priceListId?: string): Observable<PricingHintRecord[]> {
    let params = new HttpParams();
    if (priceListId) params = params.set('priceListId', priceListId);
    return this.http.get<PricingHintRecord[]>(`${this.base}/orders/${orderId}/pricing-hints`, { params });
  }

  listOrderPricingOverrides(orderId: string): Observable<PricingOverrideRecord[]> {
    return this.http.get<PricingOverrideRecord[]>(`${this.base}/orders/${orderId}/pricing-overrides`);
  }

  requestPricingOverride(orderId: string, payload: CreatePricingOverridePayload): Observable<PricingOverrideRecord> {
    return this.http.post<PricingOverrideRecord>(`${this.base}/orders/${orderId}/pricing-overrides`, payload);
  }

  approvePricingOverride(orderId: string, overrideId: string): Observable<PricingOverrideRecord> {
    return this.http.post<PricingOverrideRecord>(
      `${this.base}/orders/${orderId}/pricing-overrides/${overrideId}/approve`,
      {}
    );
  }

  rejectPricingOverride(orderId: string, overrideId: string): Observable<PricingOverrideRecord> {
    return this.http.post<PricingOverrideRecord>(
      `${this.base}/orders/${orderId}/pricing-overrides/${overrideId}/reject`,
      {}
    );
  }

  listTerritories(query?: { parentTerritoryId?: string; level?: string }): Observable<SalesTerritoryRecord[]> {
    let params = new HttpParams();
    if (query?.parentTerritoryId) params = params.set('parentTerritoryId', query.parentTerritoryId);
    if (query?.level) params = params.set('level', query.level);
    return this.http
      .get<ListApiResponse<SalesTerritoryRecord>>(`${this.base}/territories`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  getTerritory(id: string): Observable<SalesTerritoryRecord> {
    return this.http.get<SalesTerritoryRecord>(`${this.base}/territories/${id}`);
  }

  createTerritory(payload: CreateSalesTerritoryPayload): Observable<SalesTerritoryRecord> {
    return this.http.post<SalesTerritoryRecord>(`${this.base}/territories`, payload);
  }

  updateTerritory(id: string, payload: UpdateSalesTerritoryPayload): Observable<SalesTerritoryRecord> {
    return this.http.put<SalesTerritoryRecord>(`${this.base}/territories/${id}`, payload);
  }

  deleteTerritory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/territories/${id}`);
  }

  listTerritoryAssignments(
    territoryId: string,
    query?: { subjectType?: string; subjectId?: string }
  ): Observable<TerritoryAssignmentRecord[]> {
    let params = new HttpParams();
    if (query?.subjectType) params = params.set('subjectType', query.subjectType);
    if (query?.subjectId) params = params.set('subjectId', query.subjectId);
    return this.http
      .get<ListApiResponse<TerritoryAssignmentRecord>>(`${this.base}/territories/${territoryId}/assignments`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  createTerritoryAssignment(
    territoryId: string,
    payload: CreateTerritoryAssignmentPayload
  ): Observable<TerritoryAssignmentRecord> {
    return this.http.post<TerritoryAssignmentRecord>(`${this.base}/territories/${territoryId}/assignments`, payload);
  }

  updateTerritoryAssignment(id: string, payload: UpdateTerritoryAssignmentPayload): Observable<TerritoryAssignmentRecord> {
    return this.http.put<TerritoryAssignmentRecord>(`${this.base}/territory-assignments/${id}`, payload);
  }

  deleteTerritoryAssignment(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/territory-assignments/${id}`);
  }

  listQuotas(query?: { scopeType?: string; scopeId?: string }): Observable<SalesQuotaRecord[]> {
    let params = new HttpParams();
    if (query?.scopeType) params = params.set('scopeType', query.scopeType);
    if (query?.scopeId) params = params.set('scopeId', query.scopeId);
    return this.http
      .get<ListApiResponse<SalesQuotaRecord>>(`${this.base}/quotas`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  getQuota(id: string): Observable<SalesQuotaRecord> {
    return this.http.get<SalesQuotaRecord>(`${this.base}/quotas/${id}`);
  }

  createQuota(payload: CreateSalesQuotaPayload): Observable<SalesQuotaRecord> {
    return this.http.post<SalesQuotaRecord>(`${this.base}/quotas`, payload);
  }

  updateQuota(id: string, payload: UpdateSalesQuotaPayload): Observable<SalesQuotaRecord> {
    return this.http.put<SalesQuotaRecord>(`${this.base}/quotas/${id}`, payload);
  }

  deleteQuota(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/quotas/${id}`);
  }

  listQuotaConsumptions(quotaId: string): Observable<QuotaConsumptionRecord[]> {
    return this.http
      .get<ListApiResponse<QuotaConsumptionRecord>>(`${this.base}/quotas/${quotaId}/consumptions`)
      .pipe(map(response => this.unwrapList(response)));
  }

  createQuotaConsumption(quotaId: string, payload: CreateQuotaConsumptionPayload): Observable<QuotaConsumptionRecord> {
    return this.http.post<QuotaConsumptionRecord>(`${this.base}/quotas/${quotaId}/consumptions`, payload);
  }

  evaluateQuotas(payload: EvaluateSalesQuotaPayload): Observable<EvaluateSalesQuotaResult> {
    return this.http.post<EvaluateSalesQuotaResult>(`${this.base}/quotas/evaluate`, payload);
  }

  listReservations(query?: {
    status?: string;
    salesChannelId?: string;
    customerAccountId?: string;
  }): Observable<SalesReservationRecord[]> {
    let params = new HttpParams();
    if (query?.status) params = params.set('status', query.status);
    if (query?.salesChannelId) params = params.set('salesChannelId', query.salesChannelId);
    if (query?.customerAccountId) params = params.set('customerAccountId', query.customerAccountId);
    return this.http
      .get<ListApiResponse<SalesReservationRecord>>(`${this.base}/reservations`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  getReservation(id: string): Observable<SalesReservationRecord> {
    return this.http.get<SalesReservationRecord>(`${this.base}/reservations/${id}`);
  }

  createReservation(payload: CreateSalesReservationPayload): Observable<SalesReservationRecord> {
    return this.http.post<SalesReservationRecord>(`${this.base}/reservations`, payload);
  }

  releaseReservation(id: string, payload: ReleaseSalesReservationPayload): Observable<SalesReservationRecord> {
    return this.http.post<SalesReservationRecord>(`${this.base}/reservations/${id}/release`, payload);
  }

  expireDueReservations(asOfUtc?: string | null): Observable<{ expiredCount: number }> {
    return this.http.post<{ expiredCount: number }>(`${this.base}/reservations/expire-due`, { asOfUtc: asOfUtc ?? null });
  }

  listReservationHolds(reservationId: string): Observable<ReservationHoldRecord[]> {
    return this.http
      .get<ListApiResponse<ReservationHoldRecord>>(`${this.base}/reservations/${reservationId}/holds`)
      .pipe(map(response => this.unwrapList(response)));
  }

  listReservationReleases(reservationId: string): Observable<ReservationReleaseRecord[]> {
    return this.http
      .get<ListApiResponse<ReservationReleaseRecord>>(`${this.base}/reservations/${reservationId}/releases`)
      .pipe(map(response => this.unwrapList(response)));
  }

  listAllocationPolicies(): Observable<AllocationPolicyRecord[]> {
    return this.http
      .get<ListApiResponse<AllocationPolicyRecord>>(`${this.base}/allocation-policies`)
      .pipe(map(response => this.unwrapList(response)));
  }

  getAllocationPolicy(id: string): Observable<AllocationPolicyRecord> {
    return this.http.get<AllocationPolicyRecord>(`${this.base}/allocation-policies/${id}`);
  }

  createAllocationPolicy(payload: CreateAllocationPolicyPayload): Observable<AllocationPolicyRecord> {
    return this.http.post<AllocationPolicyRecord>(`${this.base}/allocation-policies`, payload);
  }

  updateAllocationPolicy(id: string, payload: UpdateAllocationPolicyPayload): Observable<AllocationPolicyRecord> {
    return this.http.put<AllocationPolicyRecord>(`${this.base}/allocation-policies/${id}`, payload);
  }

  evaluateAllocation(payload: EvaluateAllocationPayload): Observable<EvaluateAllocationResult> {
    return this.http.post<EvaluateAllocationResult>(`${this.base}/allocations/evaluate`, payload);
  }

  listCreditPolicies(): Observable<CreditPolicyRecord[]> {
    return this.http
      .get<ListApiResponse<CreditPolicyRecord>>(`${this.base}/credit-policies`)
      .pipe(map(response => this.unwrapList(response)));
  }

  getCreditPolicy(id: string): Observable<CreditPolicyRecord> {
    return this.http.get<CreditPolicyRecord>(`${this.base}/credit-policies/${id}`);
  }

  createCreditPolicy(payload: CreateCreditPolicyPayload): Observable<CreditPolicyRecord> {
    return this.http.post<CreditPolicyRecord>(`${this.base}/credit-policies`, payload);
  }

  updateCreditPolicy(id: string, payload: UpdateCreditPolicyPayload): Observable<CreditPolicyRecord> {
    return this.http.put<CreditPolicyRecord>(`${this.base}/credit-policies/${id}`, payload);
  }

  listCustomerCreditLimits(customerAccountId?: string): Observable<CustomerCreditLimitRecord[]> {
    let params = new HttpParams();
    if (customerAccountId) {
      params = params.set('customerAccountId', customerAccountId);
    }
    return this.http
      .get<ListApiResponse<CustomerCreditLimitRecord>>(`${this.base}/customer-credit-limits`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  getCustomerCreditLimit(id: string): Observable<CustomerCreditLimitRecord> {
    return this.http.get<CustomerCreditLimitRecord>(`${this.base}/customer-credit-limits/${id}`);
  }

  createCustomerCreditLimit(payload: CreateCustomerCreditLimitPayload): Observable<CustomerCreditLimitRecord> {
    return this.http.post<CustomerCreditLimitRecord>(`${this.base}/customer-credit-limits`, payload);
  }

  updateCustomerCreditLimit(id: string, payload: UpdateCustomerCreditLimitPayload): Observable<CustomerCreditLimitRecord> {
    return this.http.put<CustomerCreditLimitRecord>(`${this.base}/customer-credit-limits/${id}`, payload);
  }

  listCreditDecisions(customerAccountId?: string): Observable<CreditDecisionRecord[]> {
    let params = new HttpParams();
    if (customerAccountId) {
      params = params.set('customerAccountId', customerAccountId);
    }
    return this.http
      .get<ListApiResponse<CreditDecisionRecord>>(`${this.base}/credit-decisions`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  evaluateCredit(payload: EvaluateCreditPayload): Observable<EvaluateCreditResult> {
    return this.http.post<EvaluateCreditResult>(`${this.base}/credit/evaluate`, payload);
  }

  listInstallmentPlans(customerAccountId?: string): Observable<InstallmentPlanRecord[]> {
    let params = new HttpParams();
    if (customerAccountId) params = params.set('customerAccountId', customerAccountId);
    return this.http.get<ListApiResponse<InstallmentPlanRecord>>(`${this.base}/installments/plans`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  getInstallmentPlan(id: string): Observable<InstallmentPlanRecord> {
    return this.http.get<InstallmentPlanRecord>(`${this.base}/installments/plans/${id}`);
  }

  listInstallmentSchedules(planId: string): Observable<InstallmentScheduleRecord[]> {
    return this.http.get<ListApiResponse<InstallmentScheduleRecord>>(`${this.base}/installments/plans/${planId}/schedules`)
      .pipe(map(response => this.unwrapList(response)));
  }

  listInstallmentPayments(planId: string): Observable<InstallmentPaymentRecord[]> {
    return this.http.get<ListApiResponse<InstallmentPaymentRecord>>(`${this.base}/installments/plans/${planId}/payments`)
      .pipe(map(response => this.unwrapList(response)));
  }

  createInstallmentPlan(payload: CreateInstallmentPlanPayload): Observable<InstallmentPlanRecord> {
    return this.http.post<InstallmentPlanRecord>(`${this.base}/installments/plans`, payload);
  }

  recordInstallmentPayment(planId: string, payload: RecordInstallmentPaymentPayload): Observable<InstallmentPaymentRecord> {
    return this.http.post<InstallmentPaymentRecord>(`${this.base}/installments/${planId}/pay`, payload);
  }

  listConsignmentContracts(customerAccountId?: string): Observable<ConsignmentContractRecord[]> {
    let params = new HttpParams();
    if (customerAccountId) params = params.set('customerAccountId', customerAccountId);
    return this.http.get<ListApiResponse<ConsignmentContractRecord>>(`${this.base}/consignments/contracts`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  getConsignmentContract(id: string): Observable<ConsignmentContractRecord> {
    return this.http.get<ConsignmentContractRecord>(`${this.base}/consignments/contracts/${id}`);
  }

  listConsignmentStock(contractId: string): Observable<ConsignmentStockRecord[]> {
    return this.http.get<ListApiResponse<ConsignmentStockRecord>>(`${this.base}/consignments/contracts/${contractId}/stock`)
      .pipe(map(response => this.unwrapList(response)));
  }

  listConsignmentSettlements(contractId: string): Observable<ConsignmentSettlementRecord[]> {
    return this.http.get<ListApiResponse<ConsignmentSettlementRecord>>(`${this.base}/consignments/contracts/${contractId}/settlements`)
      .pipe(map(response => this.unwrapList(response)));
  }

  createConsignmentContract(payload: CreateConsignmentContractPayload): Observable<ConsignmentContractRecord> {
    return this.http.post<ConsignmentContractRecord>(`${this.base}/consignments/contracts`, payload);
  }

  settleConsignment(contractId: string, payload: SettleConsignmentPayload): Observable<ConsignmentSettlementRecord> {
    return this.http.post<ConsignmentSettlementRecord>(`${this.base}/consignments/${contractId}/settle`, payload);
  }

  listCollectionCases(status?: string, customerAccountId?: string): Observable<CollectionCaseRecord[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    if (customerAccountId) params = params.set('customerAccountId', customerAccountId);
    return this.http.get<ListApiResponse<CollectionCaseRecord>>(`${this.base}/collections/cases`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  getCollectionCase(id: string): Observable<CollectionCaseRecord> {
    return this.http.get<CollectionCaseRecord>(`${this.base}/collections/cases/${id}`);
  }

  listCollectionActions(caseId: string): Observable<CollectionActionRecord[]> {
    return this.http.get<ListApiResponse<CollectionActionRecord>>(`${this.base}/collections/cases/${caseId}/actions`)
      .pipe(map(response => this.unwrapList(response)));
  }

  createCollectionCase(payload: CreateCollectionCasePayload): Observable<CollectionCaseRecord> {
    return this.http.post<CollectionCaseRecord>(`${this.base}/collections/cases`, payload);
  }

  updateCollectionCase(id: string, payload: UpdateCollectionCasePayload): Observable<CollectionCaseRecord> {
    return this.http.put<CollectionCaseRecord>(`${this.base}/collections/cases/${id}`, payload);
  }

  addCollectionAction(caseId: string, payload: CreateCollectionActionPayload): Observable<CollectionActionRecord> {
    return this.http.post<CollectionActionRecord>(`${this.base}/collections/cases/${caseId}/actions`, payload);
  }

  listDistributionRoutes(query?: {
    plannedDateFromUtc?: string;
    plannedDateToUtc?: string;
    status?: string;
    assignedSellerId?: string;
  }): Observable<RoutePlanRecord[]> {
    let params = new HttpParams();
    if (query?.plannedDateFromUtc) params = params.set('plannedDateFromUtc', query.plannedDateFromUtc);
    if (query?.plannedDateToUtc) params = params.set('plannedDateToUtc', query.plannedDateToUtc);
    if (query?.status) params = params.set('status', query.status);
    if (query?.assignedSellerId) params = params.set('assignedSellerId', query.assignedSellerId);
    return this.http
      .get<ListApiResponse<RoutePlanRecord>>(`${this.distributionBase}/routes`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  getDistributionRoute(id: string): Observable<RoutePlanRecord> {
    return this.http.get<RoutePlanRecord>(`${this.distributionBase}/routes/${id}`);
  }

  listRouteVisits(routeId: string): Observable<VisitPlanRecord[]> {
    return this.http
      .get<ListApiResponse<VisitPlanRecord>>(`${this.distributionBase}/routes/${routeId}/visits`)
      .pipe(map(response => this.unwrapList(response)));
  }

  createDistributionRoute(payload: CreateRoutePlanPayload): Observable<RoutePlanRecord> {
    return this.http.post<RoutePlanRecord>(`${this.distributionBase}/routes`, payload);
  }

  getDistributionVisit(id: string): Observable<VisitPlanRecord> {
    return this.http.get<VisitPlanRecord>(`${this.distributionBase}/visits/${id}`);
  }

  createDistributionVisit(payload: CreateVisitPlanPayload): Observable<VisitPlanRecord> {
    return this.http.post<VisitPlanRecord>(`${this.distributionBase}/visits`, payload);
  }

  startVisitExecution(visitId: string): Observable<VisitPlanRecord> {
    return this.http.post<VisitPlanRecord>(`${this.distributionBase}/visits/${visitId}/start`, {});
  }

  completeVisitExecution(visitId: string, payload: CompleteVisitExecutionPayload): Observable<VisitPlanRecord> {
    return this.http.post<VisitPlanRecord>(`${this.distributionBase}/visits/${visitId}/complete`, payload);
  }

  listVanStock(routePlanId: string): Observable<VanStockBalanceRecord[]> {
    const params = new HttpParams().set('routePlanId', routePlanId);
    return this.http
      .get<ListApiResponse<VanStockBalanceRecord>>(`${this.distributionBase}/van-stock`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  listVanStockMovements(routePlanId: string): Observable<VanStockMovementRecord[]> {
    const params = new HttpParams().set('routePlanId', routePlanId);
    return this.http
      .get<ListApiResponse<VanStockMovementRecord>>(`${this.distributionBase}/van-stock/movements`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  createVanStockMovement(payload: CreateVanStockMovementPayload): Observable<VanStockMovementRecord> {
    return this.http.post<VanStockMovementRecord>(`${this.distributionBase}/van-stock/movements`, payload);
  }

  listProofsOfVisit(visitPlanId: string): Observable<ProofOfVisitRecord[]> {
    const params = new HttpParams().set('visitPlanId', visitPlanId);
    return this.http
      .get<ListApiResponse<ProofOfVisitRecord>>(`${this.distributionBase}/proofs/visit`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  createProofOfVisit(payload: CreateProofOfVisitPayload): Observable<ProofOfVisitRecord> {
    return this.http.post<ProofOfVisitRecord>(`${this.distributionBase}/proofs/visit`, payload);
  }

  listProofsOfDelivery(visitPlanId?: string): Observable<ProofOfDeliveryRecord[]> {
    let params = new HttpParams();
    if (visitPlanId) params = params.set('visitPlanId', visitPlanId);
    return this.http
      .get<ListApiResponse<ProofOfDeliveryRecord>>(`${this.distributionBase}/proofs/delivery`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  listRetailAudits(visitPlanId: string): Observable<RetailAuditRecord[]> {
    const params = new HttpParams().set('visitPlanId', visitPlanId);
    return this.http
      .get<ListApiResponse<RetailAuditRecord>>(`${this.distributionBase}/retail-audits`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  createRetailAudit(payload: CreateRetailAuditPayload): Observable<RetailAuditRecord> {
    return this.http.post<RetailAuditRecord>(`${this.distributionBase}/retail-audits`, payload);
  }

  listFieldSettlements(routePlanId?: string): Observable<FieldSettlementRecord[]> {
    let params = new HttpParams();
    if (routePlanId) params = params.set('routePlanId', routePlanId);
    return this.http
      .get<ListApiResponse<FieldSettlementRecord>>(`${this.distributionBase}/settlements`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  createFieldSettlement(payload: CreateFieldSettlementPayload): Observable<FieldSettlementRecord> {
    return this.http.post<FieldSettlementRecord>(`${this.distributionBase}/settlements`, payload);
  }

  closeFieldSettlement(id: string): Observable<FieldSettlementRecord> {
    return this.http.post<FieldSettlementRecord>(`${this.distributionBase}/settlements/${id}/close`, {});
  }

  listCommissionPlans(): Observable<CommissionPlanRecord[]> {
    return this.http
      .get<ListApiResponse<CommissionPlanRecord>>(`${this.base}/commissions/plans`)
      .pipe(map(response => this.unwrapList(response)));
  }

  getCommissionPlan(id: string): Observable<CommissionPlanRecord> {
    return this.http.get<CommissionPlanRecord>(`${this.base}/commissions/plans/${id}`);
  }

  listCommissionRules(planId: string): Observable<CommissionRuleRecord[]> {
    return this.http
      .get<ListApiResponse<CommissionRuleRecord>>(`${this.base}/commissions/plans/${planId}/rules`)
      .pipe(map(response => this.unwrapList(response)));
  }

  listCommissionPayouts(planId: string): Observable<CommissionPayoutRecord[]> {
    return this.http
      .get<ListApiResponse<CommissionPayoutRecord>>(`${this.base}/commissions/plans/${planId}/payouts`)
      .pipe(map(response => this.unwrapList(response)));
  }

  listCommissionClawbacks(planId: string): Observable<CommissionClawbackRecord[]> {
    return this.http
      .get<ListApiResponse<CommissionClawbackRecord>>(`${this.base}/commissions/plans/${planId}/clawbacks`)
      .pipe(map(response => this.unwrapList(response)));
  }

  createCommissionPlan(payload: CreateCommissionPlanPayload): Observable<CommissionPlanRecord> {
    return this.http.post<CommissionPlanRecord>(`${this.base}/commissions/plans`, payload);
  }

  calculateCommission(payload: CalculateCommissionPayload): Observable<CalculateCommissionResult> {
    return this.http.post<CalculateCommissionResult>(`${this.base}/commissions/calculate`, payload);
  }

  recordCommissionClawback(payload: RecordCommissionClawbackPayload): Observable<CommissionClawbackRecord> {
    return this.http.post<CommissionClawbackRecord>(`${this.base}/commissions/clawbacks`, payload);
  }

  listEnterpriseContracts(customerAccountId?: string): Observable<EnterpriseContractRecord[]> {
    let params = new HttpParams();
    if (customerAccountId) params = params.set('customerAccountId', customerAccountId);
    return this.http.get<ListApiResponse<EnterpriseContractRecord>>(`${this.base}/contracts`, { params })
      .pipe(map(response => this.unwrapList(response)));
  }

  getEnterpriseContract(id: string): Observable<EnterpriseContractRecord> {
    return this.http.get<EnterpriseContractRecord>(`${this.base}/contracts/${id}`);
  }

  listContractReleaseOrders(contractId: string): Observable<ContractReleaseOrderRecord[]> {
    return this.http.get<ListApiResponse<ContractReleaseOrderRecord>>(`${this.base}/contracts/${contractId}/release-orders`)
      .pipe(map(response => this.unwrapList(response)));
  }

  createEnterpriseContract(payload: CreateEnterpriseContractPayload): Observable<EnterpriseContractRecord> {
    return this.http.post<EnterpriseContractRecord>(`${this.base}/contracts`, payload);
  }

  createContractReleaseOrder(contractId: string, payload: CreateContractReleaseOrderPayload): Observable<ContractReleaseOrderRecord> {
    return this.http.post<ContractReleaseOrderRecord>(`${this.base}/contracts/${contractId}/release-orders`, payload);
  }

  listTenders(): Observable<TenderOpportunityRecord[]> {
    return this.http.get<ListApiResponse<TenderOpportunityRecord>>(`${this.base}/tenders`)
      .pipe(map(response => this.unwrapList(response)));
  }

  getTender(id: string): Observable<TenderOpportunityRecord> {
    return this.http.get<TenderOpportunityRecord>(`${this.base}/tenders/${id}`);
  }

  listTenderProposals(tenderId: string): Observable<TenderProposalRecord[]> {
    return this.http.get<ListApiResponse<TenderProposalRecord>>(`${this.base}/tenders/${tenderId}/proposals`)
      .pipe(map(response => this.unwrapList(response)));
  }

  createTender(payload: CreateTenderPayload): Observable<TenderOpportunityRecord> {
    return this.http.post<TenderOpportunityRecord>(`${this.base}/tenders`, payload);
  }

  createTenderProposal(tenderId: string, payload: CreateTenderProposalPayload): Observable<TenderProposalRecord> {
    return this.http.post<TenderProposalRecord>(`${this.base}/tenders/${tenderId}/proposals`, payload);
  }

  updateTenderStatus(tenderId: string, status: string): Observable<TenderOpportunityRecord> {
    return this.http.post<TenderOpportunityRecord>(`${this.base}/tenders/${tenderId}/status`, { status });
  }

  private unwrapList<T>(response: ListApiResponse<T>): T[] {
    return Array.isArray(response) ? response : response?.items ?? [];
  }
}
